import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronRight, Pin, PinOff, Copy, Check, History, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const HISTORY_KEY = 'live-kpi-rail:snapshot-history';
const HISTORY_MAX = 5;

/**
 * Live KPI Rail — a sticky right-rail panel that surfaces the
 * estimate's headline numbers so sales reps don't have to scroll
 * back to the top after every change.
 *
 * Renders a vertical stack of small metric cards. Collapses to a
 * 32px pin-tab on demand; collapsed state persists per page via
 * localStorage under `live-kpi-rail:${storageKey}:collapsed`.
 *
 * Snapshots: clicking the Copy button writes a one-liner to the
 * clipboard AND prepends `{line, source, ts}` to the shared global
 * history (capped at 5, dedup by line). A History button surfaces
 * the popover so reps can re-copy or jump back to a previous snapshot.
 *
 * Props:
 *   storageKey  - unique slug per host page ("output", "compare")
 *   title       - panel heading (default "Live KPIs")
 *   sourceLabel - human-readable name of the estimate / context the
 *                 snapshot came from. Falls back to `title`.
 *   metrics     - array of { label, value, hint?, accent?, testId? }
 *                 accent: 'slate' | 'cyan' | 'emerald' | 'amber' | 'rose'
 *   footer      - optional ReactNode rendered below the metrics
 */
export default function LiveKPIRail({
  storageKey,
  title = 'Live KPIs',
  sourceLabel = null,
  metrics = [],
  footer = null,
}) {
  const lsKey = `live-kpi-rail:${storageKey}:collapsed`;
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(lsKey) === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(lsKey, collapsed ? '1' : '0');
  }, [lsKey, collapsed]);

  const toggle = useCallback(() => setCollapsed(c => !c), []);

  // ──────── Snapshot history (cross-page) ─────────────────────────
  const [history, setHistory] = useState(() => readHistory());

  // Refresh history when the rail mounts/remounts (e.g. user navigated
  // away and came back) so the popover is always current.
  useEffect(() => {
    setHistory(readHistory());
  }, [storageKey]);

  // ──────── Snapshot to clipboard ─────────────────────────────────
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);
  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);
  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    const line = metrics
      .map(m => (m.value == null || m.value === '—') ? null : String(m.value))
      .filter(Boolean)
      .join(' · ');
    if (!line) return;
    try {
      await navigator.clipboard.writeText(line);
      setCopied(true);
      const entry = {
        line,
        source: (sourceLabel || title || storageKey).slice(0, 64),
        ts: new Date().toISOString(),
      };
      setHistory(pushHistory(entry));
      toast.success('KPIs copied — paste into Slack or email');
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }, [metrics, sourceLabel, title, storageKey]);

  const handleReCopy = useCallback(async (entry) => {
    try {
      await navigator.clipboard.writeText(entry.line);
      toast.success(`Snapshot from ${entry.source} re-copied`);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }, []);

  const handleClearHistory = useCallback(() => {
    setHistory(clearHistory());
    toast.success('Snapshot history cleared');
  }, []);

  if (collapsed) {
    return (
      <aside
        className="hidden xl:flex flex-col items-center justify-start sticky top-24 w-9 shrink-0"
        data-testid={`live-kpi-rail-${storageKey}-collapsed`}
      >
        <button
          onClick={toggle}
          className="group flex flex-col items-center gap-2 px-1.5 py-3 rounded-l-md border border-r-0 border-slate-200 bg-white hover:bg-cyan-50 transition-colors text-slate-500 hover:text-cyan-700 shadow-sm"
          title="Show Live KPIs"
          data-testid={`live-kpi-rail-${storageKey}-expand-btn`}
        >
          <Pin className="w-3.5 h-3.5" />
          <span className="text-[10px] uppercase tracking-wider font-semibold [writing-mode:vertical-rl] rotate-180">
            {title}
          </span>
        </button>
      </aside>
    );
  }

  return (
    <aside
      className="hidden xl:block sticky top-24 w-64 shrink-0 self-start"
      data-testid={`live-kpi-rail-${storageKey}`}
    >
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
            <Pin className="w-3 h-3 text-cyan-600" />
            {title}
          </h3>
          <div className="flex items-center gap-0.5">
            {metrics.length > 0 && (
              <button
                onClick={handleCopy}
                className="p-0.5 rounded text-slate-400 hover:text-cyan-700 hover:bg-cyan-50 transition-colors"
                title="Copy KPIs as one-line summary"
                data-testid={`live-kpi-rail-${storageKey}-copy-btn`}
              >
                {copied
                  ? <Check className="w-3.5 h-3.5 text-emerald-600" />
                  : <Copy className="w-3.5 h-3.5" />}
              </button>
            )}
            {history.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="p-0.5 rounded text-slate-400 hover:text-cyan-700 hover:bg-cyan-50 transition-colors relative"
                    title={`Snapshot history (${history.length})`}
                    data-testid={`live-kpi-rail-${storageKey}-history-btn`}
                  >
                    <History className="w-3.5 h-3.5" />
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[12px] h-3 px-0.5 rounded-full bg-cyan-600 text-white text-[8px] font-bold flex items-center justify-center"
                      data-testid={`live-kpi-rail-${storageKey}-history-count`}
                    >
                      {history.length}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-80 p-0"
                  data-testid={`live-kpi-rail-${storageKey}-history-popover`}
                >
                  <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                      Recent Snapshots
                    </span>
                    <button
                      onClick={handleClearHistory}
                      className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-rose-600 transition-colors"
                      title="Clear all snapshots"
                      data-testid={`live-kpi-rail-${storageKey}-history-clear-btn`}
                    >
                      <Trash2 className="w-3 h-3" /> Clear
                    </button>
                  </div>
                  <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                    {history.map((entry, idx) => (
                      <li
                        key={`${entry.ts}-${idx}`}
                        className="px-3 py-2 hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => handleReCopy(entry)}
                        data-testid={`live-kpi-rail-${storageKey}-history-item-${idx}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-medium text-[#0F2040] truncate max-w-[180px]" title={entry.source}>
                            {entry.source}
                          </span>
                          <span className="text-[10px] text-slate-400 shrink-0 tabular-nums">
                            {relativeTime(entry.ts)}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-600 mt-0.5 tabular-nums truncate" title={entry.line}>
                          {entry.line}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-400 text-center">
                    Click any row to re-copy
                  </div>
                </PopoverContent>
              </Popover>
            )}
            <button
              onClick={toggle}
              className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
              title="Collapse"
              data-testid={`live-kpi-rail-${storageKey}-collapse-btn`}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="divide-y divide-slate-100" data-testid={`live-kpi-rail-${storageKey}-metrics`}>
          {metrics.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-400" data-testid={`live-kpi-rail-${storageKey}-empty`}>
              No data yet.
            </div>
          ) : metrics.map((m, idx) => (
            <KPIRow key={m.testId || m.label || idx} metric={m} storageKey={storageKey} />
          ))}
        </div>
        {footer && (
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500">
            {footer}
          </div>
        )}
      </div>
    </aside>
  );
}

// ──────── Local-storage history helpers ───────────────────────────
function readHistory() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, HISTORY_MAX) : [];
  } catch {
    return [];
  }
}

function pushHistory(entry) {
  if (typeof window === 'undefined') return [entry];
  const current = readHistory();
  // Dedupe: drop any existing entry with the same line so the new one
  // surfaces at the top (most recent first).
  const deduped = current.filter(e => e.line !== entry.line);
  const next = [entry, ...deduped].slice(0, HISTORY_MAX);
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch { /* quota / private mode */ }
  return next;
}

function clearHistory() {
  if (typeof window === 'undefined') return [];
  try { window.localStorage.removeItem(HISTORY_KEY); } catch { /* */ }
  return [];
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

const ACCENT_MAP = {
  slate: 'text-slate-900',
  cyan: 'text-cyan-700',
  emerald: 'text-emerald-700',
  amber: 'text-amber-700',
  rose: 'text-rose-700',
};

function KPIRow({ metric, storageKey }) {
  const accentClass = ACCENT_MAP[metric.accent] || ACCENT_MAP.slate;
  return (
    <div
      className="px-3 py-2.5"
      data-testid={`live-kpi-rail-${storageKey}-row-${metric.testId || metric.label?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {metric.label}
      </div>
      <div className={`text-lg font-bold tabular-nums mt-0.5 ${accentClass}`}>
        {metric.value ?? '—'}
      </div>
      {metric.hint && (
        <div className="text-[10px] text-slate-400 mt-0.5">{metric.hint}</div>
      )}
    </div>
  );
}

/** Convenience helper — small button + state hook the host page can
 * use to surface a "Show Live KPIs" toggle in its own header. Most
 * pages won't need this since the rail self-manages collapse. */
export function useLiveKPIToggle(storageKey) {
  const lsKey = `live-kpi-rail:${storageKey}:collapsed`;
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(lsKey) === '1';
  });

  const setAndPersist = useCallback((next) => {
    setCollapsed(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(lsKey, next ? '1' : '0');
    }
  }, [lsKey]);

  return [collapsed, setAndPersist];
}

export { Pin, PinOff };
